/* eslint-disable @typescript-eslint/no-explicit-any */
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { configureAxios } from 'apps/marsinet/src/app/utils/utils';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../context/ToastProvider';
import ModalSuggestDeviceState from 'apps/marsinet/src/app/components/modal-suggest-device-state/modal-suggest-device-state';
import { useSuggestDeviceState } from 'apps/marsinet/src/app/components/modal-suggest-device-state/use-suggest-device-state';
import MaintenanceOrdersList from './components/maintenance-orders/maintenance-order-list';
import {
  CreateIncidenceDTO,
  CreateMaintenanceOrderDto,
  IncidenceTableDto,
} from '../../../typescript-axios';
import IncidentsList from './components/incidences/incidence-list';
import ModalCreateIncidence from './components/incidences/create-incidence-modal';
import ModalCreateMaintenanceOrder from './components/maintenance-orders/create-maintenance-order-modal';
import {
  getCalendarDateInTimeZone,
  getUtcDayRange,
} from '../../../utils/timezone-utils';

/**
 * Both tabs are filtered from the same toolbar, so the scope type and the
 * default range live here rather than inside either list.
 */
export type ListStatusScope = 'open' | 'closed';

const DEFAULT_RANGE_IN_MONTHS = 1;

const buildDefaultDateRange = (timeZone: string): Date[] => {
  const to = getCalendarDateInTimeZone(new Date(), timeZone);
  const from = new Date(to);

  from.setMonth(from.getMonth() - DEFAULT_RANGE_IN_MONTHS);

  return [from, to];
};

export function Incidents() {
  const { t } = useTranslation();
  const { showToast: show } = useToast();
  const { setError, access_token, selectedTimezone } =
    useContext(MarsinetContext);

  const [activeIndex, setActiveIndex] = useState(0);
  const [incidents, setIncidents] = useState<IncidenceTableDto[]>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const { suggestForDevice, modalProps } = useSuggestDeviceState('in_maintenance');

  const [statusScope, setStatusScope] = useState<ListStatusScope>('open');
  const [dateRange, setDateRange] = useState<Date[] | null>(
    () => buildDefaultDateRange(selectedTimezone)
  );
  const [clearFiltersKey, setClearFiltersKey] = useState(0);

  const [visibleIncidenceModal, setVisibleIncidenceModal] = useState(false);
  const [visibleMaintenanceOrderModal, setVisibleMaintenanceOrderModal] =
    useState(false);

  const [maintenanceOrdersRefreshKey, setMaintenanceOrdersRefreshKey] =
    useState(0);

  const { minSelectableDate, maxSelectableDate } = useMemo(() => {
    const today = getCalendarDateInTimeZone(new Date(), selectedTimezone);
    const oneYearAgo = new Date(today);

    oneYearAgo.setFullYear(today.getFullYear() - 1);

    return { minSelectableDate: oneYearAgo, maxSelectableDate: today };
  }, [selectedTimezone]);

  const reportError = useCallback(
    (error: any) => {
      if (error?.response?.status === 401) return;

      setError(true);

      logEvent(analytics, 'error_marsinet', {
        error_message: error?.message,
        error_code: error?.response?.status,
        error_page: window.location.pathname,
        error_stack: error?.stack || 'No stack trace',
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
      });
    },
    [setError]
  );

  useEffect(() => {
    if (!access_token) return;

    configureAxios(access_token);
  }, [access_token]);

  const loadIncidents = useCallback(async () => {
    if (!access_token) return;

    const [dateFromValue, dateToValue] = dateRange || [];
    const hasRange = Boolean(dateFromValue && dateToValue);
    // Convierte los días elegidos en límites UTC. Como RPS solo filtra por fecha,
    // el navegador ajusta después los resultados usando los instantes UTC exactos.
    const rangeStart = dateFromValue && dateToValue
      ? getUtcDayRange(dateFromValue, selectedTimezone).start
      : undefined;
    const rangeEnd = dateFromValue && dateToValue
      ? getUtcDayRange(dateToValue, selectedTimezone).endExclusive
      : undefined;

    setLoadingIncidents(true);

    try {
      const response: any =
        await apiServices.incidenceApi.incidenceControllerGetAllIncidences(
          statusScope,
          rangeStart?.toISOString().slice(0, 10),
          rangeEnd
            ? new Date(rangeEnd.getTime() - 1).toISOString().slice(0, 10)
            : undefined
        );

      const list: IncidenceTableDto[] = Array.isArray(response?.data)
        ? response.data
        : [];
      const filteredList =
        rangeStart && rangeEnd
          ? list.filter((incidence) => {
              const value = incidence.start_time;
              const timestamp = value
                ? new Date(String(value)).getTime()
                : NaN;
              return (
                !Number.isNaN(timestamp) &&
                timestamp >= rangeStart.getTime() &&
                timestamp < rangeEnd.getTime()
              );
            })
          : list;

      setIncidents(filteredList);
    } catch (error: any) {
      console.error('[Incidents] Error loading RPS incidences:', error);
      reportError(error);
    } finally {
      setLoadingIncidents(false);
    }
  }, [
    access_token,
    dateRange,
    selectedTimezone,
    statusScope,
    reportError,
  ]);

  useEffect(() => {
    if (activeIndex !== 0) return;

    // A range calendar emits [from, null] on the first click; skipping that
    // pass avoids a request that a second click would immediately replace.
    const [dateFromValue, dateToValue] = dateRange || [];
    if (dateRange && (!dateFromValue || !dateToValue)) return;

    loadIncidents();
  }, [activeIndex, dateRange, loadIncidents]);

  const createIncidenceFunction = async (values: CreateIncidenceDTO, deviceId?: string) => {
    try {
      const response =
        await apiServices.incidenceApi.incidenceControllerCreateIncidence(
          values
        );

      show('success', t('messages.success'), t('messages.incidence.create'));
      await loadIncidents();
      suggestForDevice(deviceId);
      return response.data.id;
    } catch (error: any) {
      // show(
      //   'error',
      //   t('messages.error'),
      //   error?.response?.data?.message || t('messages.incidence.create_error')
      // );

      throw error;
    }
  };

  const createMaintenanceOrderFunction = async (
    values: CreateMaintenanceOrderDto, deviceId?: string
  ) => {
    try {
      const response =
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerCreateMaintenanceOrder(
          values
        );

      show(
        'success',
        t('messages.success'),
        t('messages.maintenance_orders.create')
      );

      setMaintenanceOrdersRefreshKey((prev) => prev + 1);
      await loadIncidents();
      suggestForDevice(deviceId);

      return response.data;
    } catch (error: any) {
      console.error('[Incidents] Error creating maintenance order:', error);

      // show(
      //   'error',
      //   t('messages.error'),
      //   t('messages.maintenance_orders.create_error')
      // );

      throw error;
    }
  };

  const statusScopeOptions = useMemo(
    () => [
      { label: t('pages.incidents.statusScope.open'), value: 'open' },
      { label: t('pages.incidents.statusScope.closed'), value: 'closed' },
    ],
    [t]
  );

  const handleClearFilters = () => {
    setDateRange(buildDefaultDateRange(selectedTimezone));
    setStatusScope('open');
    // Column filters belong to each DataTable; this asks them to reset.
    setClearFiltersKey((prev) => prev + 1);
  };

  const handleCreateClick = () => {
    if (activeIndex === 0) {
      setVisibleIncidenceModal(true);
      return;
    }

    setVisibleMaintenanceOrderModal(true);
  };

  // Built once here and handed to both lists as their DataTable header, so the
  // controls sit exactly where each list's own header used to be.
  const filtersToolbar = (
    <div className="flex flex-column sm:flex-row justify-content-between align-items-stretch sm:align-items-center gap-3">
      <div className="flex flex-column sm:flex-row gap-2 align-items-stretch sm:align-items-center">
        <Calendar
          value={dateRange as any}
          onChange={(e) => setDateRange((e.value as Date[]) || null)}
          selectionMode="range"
          readOnlyInput
          showIcon
          dateFormat="dd/mm/yy"
          placeholder={t('pages.incidents.lastMonth')}
          minDate={minSelectableDate}
          maxDate={maxSelectableDate}
          className="w-full sm:w-20rem"
        />

        <Button
          type="button"
          icon="pi pi-filter-slash"
          label={t('clear')}
          outlined
          className="w-full sm:w-auto text-xs sm:text-sm px-3 py-2 lg:px-4 lg:py-2"
          onClick={handleClearFilters}
        />
      </div>

      <Dropdown
        value={statusScope}
        options={statusScopeOptions}
        onChange={(e) => setStatusScope(e.value as ListStatusScope)}
        placeholder={t('pages.incidents.statusScope.placeholder')}
        className="w-full sm:w-14rem"
      />
    </div>
  );

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      <div className="col-12">
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div className="font-medium text-lg sm:text-xl md:text-2xl text-900">
            {t('pages.menu.incidentsOMtitle')}
          </div>

          <Button
            icon="pi pi-plus"
            label={t(
              activeIndex === 0
                ? 'pages.incidents.create'
                : 'pages.incidents.createOrder'
            )}
            severity="secondary"
            onClick={handleCreateClick}
            className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
          />
        </div>
      </div>

      <div className="col-12 grid mx-0">
        <div className="surface-card border-round-xl shadow-3 px-3 col-12">
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <TabPanel header={t('pages.menu.titleIncidents')}>
              <IncidentsList
                incidents={incidents}
                loading={loadingIncidents}
                clearFiltersKey={clearFiltersKey}
                header={filtersToolbar}
              />
            </TabPanel>

            <TabPanel header={t('pages.menu.maintenance_orders_tab')}>
              <MaintenanceOrdersList
                refreshKey={maintenanceOrdersRefreshKey}
                status={statusScope}
                dateRange={dateRange}
                clearFiltersKey={clearFiltersKey}
                header={filtersToolbar}
              />
            </TabPanel>
          </TabView>
        </div>
      </div>

      <ModalCreateIncidence
        visible={visibleIncidenceModal}
        setVisible={setVisibleIncidenceModal}
        incidenceFunction={createIncidenceFunction}
      />

      <ModalCreateMaintenanceOrder
        visible={visibleMaintenanceOrderModal}
        setVisible={setVisibleMaintenanceOrderModal}
        maintenanceOrderFunction={createMaintenanceOrderFunction}
        maintenanceTypeRestriction="preventive-scheduled"
      />

      <ModalSuggestDeviceState {...modalProps} />
    </div>
  );
}

export default Incidents;
